import { z } from 'zod';

// Password validation function
const googlePasswordValidation = (password: string) => {
  // Check length
  if (password.length < 8) {
    return 'Password must be at least 8 characters long';
  }

  // Check for uppercase letter
  if (!/[A-Z]/.test(password)) {
    return 'Password must contain at least one uppercase letter';
  }

  // Check for lowercase letter
  if (!/[a-z]/.test(password)) {
    return 'Password must contain at least one lowercase letter';
  }

  // Check for number
  if (!/\d/.test(password)) {
    return 'Password must contain at least one number';
  }

  // Check for special character
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return 'Password must contain at least one special character';
  }

  // Check for common passwords
  const commonPasswords = [
    'password123',
    'qwerty123',
    '12345678',
    'admin123',
    'welcome1',
    'letmein1',
    'password1',
    '123456789',
    'abc123',
    '11111111'
  ];
  if (commonPasswords.includes(password.toLowerCase())) {
    return 'Password is too common. Please choose a stronger password';
  }

  // Check for sequential characters
  if (/(.)\1{2,}/.test(password)) {
    return 'Password cannot contain 3 or more repeated characters';
  }

  // Check for sequential numbers
  if (/012|123|234|345|456|567|678|789/.test(password)) {
    return 'Password cannot contain sequential numbers';
  }

  return true;
};

// Client Schemas (safe for frontend)
export const clientUserSchema = z.object({
  id: z.string().uuid('ID must be a valid UUID'),
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email'),
  phone: z.string().optional().nullable(),
  avatar: z.string().optional().nullable(),
  bio: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  role: z.enum(['client', 'agent']).default('client'),
  fcmToken: z.string().optional().nullable(),
  notificationPreferences: z
    .object({
      transactionUpdates: z.boolean().default(true),
      appointments: z.boolean().default(true),
      messages: z.boolean().default(true),
    })
    .optional()
    .nullable(),
  accessToken: z.string().optional(),
});

export const clientAppointmentSchema = z.object({
  id: z.number().int().positive().optional(),
  title: z.string().min(1, 'Title is required'),
  startTime: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid ISO date string for startTime',
  }),
  endTime: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid ISO date string for endTime',
  }),
  attendees: z.array(z.string().uuid('Attendee ID must be a valid UUID')).optional().default([]),
  location: z.string().optional(),
  transactionId: z.number().int().positive().optional().nullable(),
  notes: z.string().optional().nullable(),
  message: z.string().optional().nullable(),
  reminderSent: z.boolean().default(false),
  status: z.enum(['pending','accepted','declined']).optional().default('pending'),
  userId: z.string().uuid().optional(),
  acceptedBy: z.array(z.string().uuid()).optional().default([]),
  declinedBy: z.array(z.string().uuid()).optional().default([]),
});

export const clientPropertySchema = z.object({
  id: z.string().uuid('ID must be a valid UUID'),
  address: z.object({
    street: z.string().min(1, 'Street is required'),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
  }),
  price: z.number().positive('Price must be positive'),
  imageUrl: z.string().optional().nullable(),
  type: z.string().optional(),
  details: z
    .object({
      bedrooms: z.number().int().positive().optional(),
      bathrooms: z.number().int().positive().optional(),
      squareFeet: z.number().int().positive().optional(),
      yearBuilt: z.number().int().positive().optional(),
    })
    .optional()
    .nullable(),
  createdAt: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid ISO date string for createdAt',
  }),
});

export const clientNotificationSchema = z.object({
  id: z.string().uuid('ID must be a valid UUID'),
  userId: z.string().uuid('User ID must be a valid UUID'),
  title: z.string().min(1, 'Title is required'),
  message: z.string().min(1, 'Message is required'),
  type: z.string().min(1, 'Type is required'),
  relatedId: z.string().uuid().optional().nullable(),
  read: z.boolean().default(false),
  createdAt: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid ISO date string for createdAt',
  }),
});

export const clientTransactionSchema = z.object({
  id: z.number().int().positive(),
  clientId: z.string().uuid('Client ID must be a valid UUID'),
  agentId: z.string().uuid('Agent ID must be a valid UUID'),
  propertyId: z.string().uuid('Property ID must be a valid UUID'),
  status: z.string().min(1, 'Status is required'),
  progress: z.number().int().min(0).max(100).default(0),
  startDate: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid ISO date string for startDate',
  }),
  closingDate: z.string().optional().nullable(),
  details: z
    .object({
      loanStatus: z.string().optional(),
      inspectionStatus: z.string().optional(),
      titleStatus: z.string().optional(),
      appraisalStatus: z.string().optional(),
    })
    .optional()
    .nullable(),
});

export const clientMessageSchema = z.object({
  id: z.number().int().positive(),
  senderId: z.string().uuid('Sender ID must be a valid UUID'),
  receiverId: z.string().uuid('Receiver ID must be a valid UUID'),
  content: z.string().min(1, 'Content is required'),
  read: z.boolean().default(false),
  createdAt: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid ISO date string for createdAt',
  }),
});

// Insert Schemas (for creating database records)
export const insertUserSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters long'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string().refine(googlePasswordValidation, {
    message: 'Password does not meet requirements'
  }),
  role: z.enum(['client', 'agent']),
  phone: z.string().optional(),
  avatar: z.string().optional().nullable(),
  bio: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  fcmToken: z.string().optional().nullable(),
  notificationPreferences: z
    .object({
      transactionUpdates: z.boolean().default(true),
      appointments: z.boolean().default(true),
      messages: z.boolean().default(true),
    })
    .optional()
    .nullable(),
  address: z.object({
    street: z.string(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string()
  }).optional()
});

export const insertPropertySchema = z.object({
  address: z.object({
    street: z.string().min(1, 'Street is required'),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
  }),
  price: z.number().positive('Price must be positive'),
  imageUrl: z.string().optional().nullable(),
  type: z.string().optional(),
  details: z
    .object({
      bedrooms: z.number().int().positive().optional(),
      bathrooms: z.number().int().positive().optional(),
      squareFeet: z.number().int().positive().optional(),
      yearBuilt: z.number().int().positive().optional(),
    })
    .optional()
    .nullable(),
});

export const insertTransactionSchema = z.object({
  clientId: z.string().uuid('Client ID must be a valid UUID'),
  agentId: z.string().uuid('Agent ID must be a valid UUID'),
  propertyId: z.string().uuid('Property ID must be a valid UUID'),
  status: z.string().min(1, 'Status is required'),
  progress: z.number().int().min(0).max(100).optional().default(0),
  startDate: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid ISO date string for startDate',
  }),
  closingDate: z.string().optional().nullable(),
  details: z
    .object({
      loanStatus: z.string().optional(),
      inspectionStatus: z.string().optional(),
      titleStatus: z.string().optional(),
      appraisalStatus: z.string().optional(),
    })
    .optional()
    .nullable(),
});

export const insertAppointmentSchema = z.object({
  userId: z.string().uuid('User ID must be a valid UUID'),
  title: z.string().min(1, 'Title is required'),
  startTime: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid ISO date string for startTime',
  }),
  endTime: z.string().refine((val) => !isNaN(Date.parse(val)), {
    message: 'Invalid ISO date string for endTime',
  }),
  location: z.string().min(1, 'Location is required'),
  transactionId: z.number().int().positive().optional().nullable(),
  attendees: z.array(z.string().uuid('Attendee ID must be a valid UUID')).optional().default([]),
  notes: z.string().optional().nullable(),
  status: z.enum(['pending','accepted','declined']).optional().default('pending'),
});

export const insertNotificationSchema = z.object({
  userId: z.string().uuid('User ID must be a valid UUID'),
  title: z.string().min(1, 'Title is required'),
  message: z.string().min(1, 'Message is required'),
  type: z.string().min(1, 'Type is required'),
  relatedId: z.string().uuid().optional().nullable(),
});

export const insertMessageSchema = z.object({
  senderId: z.string().uuid('Sender ID must be a valid UUID'),
  receiverId: z.string().uuid('Receiver ID must be a valid UUID'),
  content: z.string().min(1, 'Content is required'),
});

// Type definitions for schemas
export type ClientUser = z.infer<typeof clientUserSchema>;
export type ClientAppointment = z.infer<typeof clientAppointmentSchema>;
export type ClientNotification = z.infer<typeof clientNotificationSchema>;
export type ClientTransaction = z.infer<typeof clientTransactionSchema>;
export type ClientProperty = z.infer<typeof clientPropertySchema>;
export type ClientMessage = z.infer<typeof clientMessageSchema>;
export type InsertUser = z.infer<typeof insertUserSchema>;